/**
 * Common functions for printing tools.
 */

import appBase from './index.js';
import appPrintCustomizer from './schmprint/SchmPrintCustomizer.js';
import {getHeight, getWidth} from "ol/extent";
import {METERS_PER_INCH, POINTS_PER_INCH, POINTS_PER_METER, SWISSTOPO_PIXELS_PER_METER} from "./constants";
import {NO_HTTP_CREDENTIALS} from "./utils";
import {resolutionDependentURL} from './basemaps.js';
import {
  HIGHLIGHT_SPACING,
  HIGHLIGHT_SPACING_SMALL,
  HIGHLIGHT_WIDTH,
  HIGHLIGHT_WIDTH_SMALL,
  PIXELS_PER_POINT
} from "./constants";

/**
 * @param {app.print.Service} appPrintService
 * @param {app.detour.Service} appDetourService
 * @param {app.waiting} appWaiting
 * @param {!angular.$http} $http
 * @param {string} apiUrl
 * @constructor
 * @ngInject
 */
class PrintHelper {
  constructor(appPrintService, appDetourService, appWaiting, $http, apiUrl) {

    /**
     * @type {app.print.Service}
     * @private
     */
    this.printService_ = appPrintService;

    /**
     * @private
     */
    this.detourService_ = appDetourService;

    /**
     * @private
     */
    this.appWaiting_ = appWaiting;

    this.http_ = $http;

    this.apiUrl_ = apiUrl;

    /**
     * @const {Array.<number>}
     */
    this.SCALES = [10000, 20000, 25000, 50000,
      100000, 200000, 500000, 1000000, 1400000];
  }

  /**
   * @param {string} layout
   * @param {ol.Extent} extent
   * @return {number}
   */
  computeScaleForExtent(layout, extent) {
    const width = getWidth(extent);
    const height = getHeight(extent);
    return this.computeScale(layout, width, height, true);
  }

  /**
   * @param {string} layout
   * @return {ol.Size}
   */
  getPaperSize(layout) {
    if (layout.includes('portrait')) {
      return [510, 710];
    } else {
      console.assert(layout.includes('landscape'));
      return [762, 479];
    }
  }

  /**
   * @param {string} layout
   * @param {number} width
   * @param {number} height
   * @param {boolean=} contain
   * @return {number}
   */
  computeScale(layout, width, height, contain = false) {
    console.assert(this.getPaperSize(layout));
    const paperSize = this.getPaperSize(layout);

    const scaleWidth = width / METERS_PER_INCH *
        POINTS_PER_INCH / paperSize[0];
    const scaleHeight = height / METERS_PER_INCH *
        POINTS_PER_INCH / paperSize[1];
    const pageScale = contain ? Math.max(scaleWidth, scaleHeight) : Math.min(scaleWidth, scaleHeight);

    let optimal = this.SCALES[0];
    const scales = this.SCALES;
    for (let i = 0, n = scales.length; i < n; i++) {
      // If contain is true, we look for the scale immediately above
      // the page scale, else immediately below.
      if (pageScale > scales[i]) {
        if (contain) {
          if (i + 1 === n) {
            break;
          }
          optimal = scales[i + 1];
        } else {
          optimal = scales[i];
        }
      } else {
        break;
      }
    }
    return optimal;
  }

  /**
   * @param {import('ol/Map.js').default} map
   * @return {string[]}
   */
  getActiveLands_(map) {
    const lands = [];

    map.getLayers().forEach(layer => {
      if (layer.get('kind') === 'mvt_route_layer' && layer.getVisible() && layer.getOpacity() > 0) {
        lands.push(layer.get('land'));
      }
    });
    return lands;
  }

  /**
   * @param {string} layout
   * @return {boolean}
   */
  isSlowupLayerActive(layout) {
    return layout.includes('slowUp');
  }

  /**
   * @param {ol.Map} map
   * @param {string} title
   * @param {string} layout
   * @param {number} scale
   * @param {ol.Coordinate=} overrideCenter
   * @param {Array.<Object>=} datasource
   */
  async print(map, title, layout, scale, overrideCenter, datasource) {
    if (this.appWaiting_.isWaiting()) {
      // already printing
      return;
    }

    this.appWaiting_.increment();

    const resolution = scale / SWISSTOPO_PIXELS_PER_METER;
    const dpi = SWISSTOPO_PIXELS_PER_METER * METERS_PER_INCH;
    const format = 'pdf';
    const customAttributes = {};

    const extent = this.getPrintExtent_(map, layout, scale, overrideCenter);
    this.setupMapProperties_(map, extent);


    this.detourService_.getDetoursLayers().then(async detours => {
      const detourLayers = Object.values(detours);
      const customizer = new appPrintCustomizer({
        resolution,
        extent,
        detourLayers,
        HIGHLIGHT_SPACING,
        HIGHLIGHT_SPACING_SMALL,
        HIGHLIGHT_WIDTH,
        HIGHLIGHT_WIDTH_SMALL,
        PIXELS_PER_POINT,
        resolutionDependentURL
      });

      /**
       * @type {MapFishPrintSpec}
       */
      const spec = await this.printService_.createSpec(map, scale, resolution, dpi, layout, format, customAttributes, customizer);

      map.setProperties({'isPrinting': false});

      if (overrideCenter) {
        spec.attributes.map.center = overrideCenter;
      }

      // The default title is display only for the slowup print template.
      if (title && !this.isSlowupLayerActive(layout)) {
        spec.attributes.title = title;
      }

      if (datasource) {
        spec.attributes.datasource = datasource;
      }

      // move vector layers above
      let layers = spec.attributes.map.layers;
      console.assert(layers);

      if (scale >= 200000) {
        // remove detour from spec
        layers = layers.filter(layer => {
          if (layer.geoJson && layer.name) {
            return !layer.name.includes('detour');
          } else {
            return true;
          }
        });
      }

      layers.sort((a, b) => {
        const aorder = a.type === 'geojson' ? 1 : 0;
        const border = b.type === 'geojson' ? 1 : 0;
        return border - aorder;
      });

      spec.attributes.map.layers = layers;

      this.printService_.createReport(spec, NO_HTTP_CREDENTIALS).then(
        this.createReportSuccess_.bind(this),
        this.createReportFailure_.bind(this)
      );
    });
  }

  /**
   * @param {ol.Map} map
   * @param {string} layout
   * @param {number} scale
   * @param {ol.Coordinate=} overrideCenter
   * @return {ol.Extent}
   * @private
   */
  getPrintExtent_(map, layout, scale, overrideCenter) {
    const center = overrideCenter ? overrideCenter : map.getView().getCenter();
    const paperSizePoints = this.getPaperSize(layout);
    const [paperWidthMeters, paperHeightMeters] = paperSizePoints.map(side =>
      side / POINTS_PER_METER * scale / 2
    );

    return [
      center[0] - paperWidthMeters,
      center[1] - paperHeightMeters,
      center[0] + paperWidthMeters,
      center[1] + paperHeightMeters
    ];
  }

  /**
   * Set properties on map used by print-specific styles.
   * @param {ol.Map} map
   * @param {import('ol/extent.js').Extent} extent
   * @private
   */
  setupMapProperties_(map, extent) {
    map.setProperties({
      'isPrinting': true,
      'printExtent': extent
    });
  }

  /**
   * @param {!angular.$http.Response} response Response.
   * @private
   */
  createReportSuccess_(response) {
    this.getStatus_(response.data.ref);
  }

  /**
   * @param {string} ref Ref.
   * @private
   */
  getStatus_(ref) {
    this.printService_.getStatus(ref, NO_HTTP_CREDENTIALS).then(
      this.getStatusSuccess_.bind(this, ref),
      this.getStatusError_.bind(this)
    );
  }

  /**
   * @param {string} ref Ref.
   * @param {!angular.$http.Response} response Response.
   * @private
   */
  getStatusSuccess_(ref, response) {
    if (response.data.done) {
      this.appWaiting_.decrement();
      window.location.href = this.printService_.getReportUrl(ref);
    } else {
      // not ready yet
      window.setTimeout(function() {
        this.getStatus_(ref);
      }.bind(this), 1000);
    }
  }

  /**
   * @param {!angular.$http.Response} response Response.
   * @private
   */
  getStatusError_(response) {
    this.appWaiting_.decrement();
  }

  /**
   * @param {!angular.$http.Response} response Response.
   * @private
   */
  createReportFailure_(response) {
    this.appWaiting_.decrement();
  }
}


appBase.service('appPrintHelper', PrintHelper);


export default PrintHelper;
